import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import debounce from "lodash.debounce";
import ReactPlayerMain from "react-player";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import Lottie from "lottie-react";
import { Form, Field } from "react-final-form";
import { Autocomplete } from "mui-rff";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import {
  Add,
  Archive,
  Business,
  Delete,
  PlayArrow,
  Public,
  Search,
  SmartDisplay,
  Tune,
  VideocamOutlined,
  YouTube,
} from "@mui/icons-material";

import Header from "../Header/header";
import PlaylistModal from "./PlaylistModal";
import CreatePlaylistModal from "./CreatePlaylistModal";
import styles from "./VideoManager.module.scss";
import videoLoadingAnimation from "../../animation/Animation - 1741781499593.json";
import VideoTutorialOverlay from "./VideoTutorialOverlay";

type PlaylistVideo = {
  id?: number | string;
  title: string;
  url: string;
  thumbnail?: string;
  description?: string;
  platform?: string;
  duration?: string;
  type?: string;
};

type YouTubeSearchVideo = PlaylistVideo & {
  channelTitle?: string;
  publishedAt?: string;
};

type PlaylistCategory = {
  id: number | string;
  name: string;
  type?: string;
  level?: "user" | "practice" | "global" | string;
  treatment_category_id?: number | string | null;
  treatment_ids?: number[];
  is_default?: boolean;
  is_archived?: boolean;
  videos: PlaylistVideo[];
};

type TreatmentOption = {
  label: string;
  value: number;
  [key: string]: any;
};

const getLevelLabel = (level?: string) => {
  if (level === "global") return "Global";
  if (level === "practice") return "Practice";
  return "User";
};

const getLevelClassName = (level?: string) => {
  if (level === "global") return styles.globalBadge;
  if (level === "practice") return styles.practiceBadge;
  return styles.userBadge;
};

const isLikelyUrl = (value: string) => {
  try {
    const parsed = new URL(value.trim());
    return Boolean(parsed.protocol && parsed.hostname);
  } catch {
    return false;
  }
};

const getYoutubeVideoId = (url: string) => {
  try {
    const parsed = new URL(url.trim());
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      return parsed.pathname.split("/").filter(Boolean)[0] || "";
    }

    if (host.includes("youtube.com")) {
      if (parsed.searchParams.get("v"))
        return parsed.searchParams.get("v") || "";

      const parts = parsed.pathname.split("/").filter(Boolean);
      const videoPathKeys = ["shorts", "embed", "live"];
      const keyIndex = parts.findIndex((part) => videoPathKeys.includes(part));

      if (keyIndex >= 0 && parts[keyIndex + 1]) return parts[keyIndex + 1];
    }
  } catch {
    return "";
  }

  return "";
};

const isYoutubeLink = (value: string) => {
  if (!isLikelyUrl(value)) return false;
  return Boolean(getYoutubeVideoId(value));
};

const createYoutubeFallbackFromUrl = (
  url: string,
): YouTubeSearchVideo | null => {
  const videoId = getYoutubeVideoId(url);
  if (!videoId) return null;

  return {
    id: videoId,
    title: "YouTube video",
    url: `https://www.youtube.com/watch?v=${videoId}`,
    thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    platform: "youtube",
    duration: "0:00",
    type: "youtube",
  };
};

const normalizeYoutubeSearchResponse = (
  responseData: any,
  searchValue: string,
): YouTubeSearchVideo[] => {
  const rawItems = Array.isArray(responseData)
    ? responseData
    : responseData?.items ||
      responseData?.results ||
      responseData?.videos ||
      [];

  const normalized = rawItems
    .map((item: any) => {
      const videoId =
        item.videoId ||
        item.video_id ||
        item.id?.videoId ||
        (typeof item.id === "string" ? item.id : "") ||
        getYoutubeVideoId(item.url || item.link || "");

      const url =
        item.url ||
        item.link ||
        (videoId ? `https://www.youtube.com/watch?v=${videoId}` : "");
      const thumbnail =
        item.thumbnail ||
        item.thumbnailUrl ||
        item.thumbnails?.high?.url ||
        item.thumbnails?.medium?.url ||
        item.snippet?.thumbnails?.high?.url ||
        item.snippet?.thumbnails?.medium?.url ||
        (videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : "");

      if (!url) return null;

      return {
        id: videoId || (typeof item.id === "string" ? item.id : url),
        title: item.title || item.snippet?.title || "YouTube video",
        description: item.description || item.snippet?.description || "",
        url,
        thumbnail,
        platform: "youtube",
        duration: item.duration || item.contentDetails?.duration || "0:00",
        type: "youtube",
        channelTitle:
          item.channelTitle ||
          item.channel_title ||
          item.snippet?.channelTitle ||
          "YouTube",
        publishedAt: item.publishedAt || item.snippet?.publishedAt || "",
      } as YouTubeSearchVideo;
    })
    .filter(Boolean) as YouTubeSearchVideo[];

  if (normalized.length > 0) return normalized;

  const fallbackVideo = createYoutubeFallbackFromUrl(searchValue);
  return fallbackVideo ? [fallbackVideo] : [];
};

const normalizePlaylistResponse = (responseData: any): PlaylistCategory[] => {
  const normalizedItems = Array.isArray(responseData)
    ? responseData
    : [
        ...(responseData.default_user_playlists || []).map((item: any) => ({
          ...item,
          type: "default_user_playlists",
        })),
        ...(responseData.default_practice_playlists || []).map((item: any) => ({
          ...item,
          type: "default_practice_playlists",
        })),
        ...(responseData.global_playlists || []).map((item: any) => ({
          ...item,
          type: "global_playlists",
        })),
        ...(responseData.non_default_playlists || []).map((item: any) => ({
          ...item,
          type: "non_default_playlists",
        })),
      ];

  return normalizedItems.map((item: any) => {
    const videos =
      item.playist_items || item.playlist_items || item.videos || [];
    const firstVideoTitle =
      videos.length > 0
        ? videos[0].title || `Playlist ${item.id}`
        : `Playlist ${item.id}`;

    return {
      id: item.id,
      name: item.title || item.name || firstVideoTitle,
      type: item.type,
      level: item.level,
      treatment_category_id: item.treatment_category_id,
      treatment_ids: item.treatment_ids || [],
      is_default: item.is_default,
      is_archived: item.is_archived,
      videos: videos.map((video: any) => ({
        id: video.id,
        title: video.title || "Untitled video",
        url: video.url,
        thumbnail: video.thumbnail || "",
        description: video.description || "",
        platform: video.platform || "youtube",
        duration: video.duration || "00:00:00",
        type: video.type || "mp4",
      })),
    };
  });
};

const VideoManager: React.FC = () => {
  const [categories, setCategories] = useState<PlaylistCategory[]>([]);
  const [currentCategory, setCurrentCategory] =
    useState<PlaylistCategory | null>(null);
  const [currentVideo, setCurrentVideo] = useState<PlaylistVideo | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [treatments, setTreatments] = useState<TreatmentOption[]>([]);
  const [selectedTreatment, setSelectedTreatment] = useState<string>("");
  const [userid, setUserId] = useState("");
  const [practiceId, setPracticeId] = useState("");
  const [openAddVideoModal, setOpenAddVideoModal] = useState(false);
  const [openCreatePlaylistModal, setOpenCreatePlaylistModal] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    number | string | null
  >(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [level, setLevel] = useState("");
  const [isArchived, setIsArchived] = useState(false);
  const [itemId, setItemId] = useState<number | string>("");
  const [filter, setFilter] = useState("all");
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState<number[]>(
    [],
  );
  const [selectedTreatmentIdsNew, setSelectedTreatmentIdsNew] = useState<
    number[]
  >([]);
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentOption[]>([]);
  const [lastPlayedSeconds, setLastPlayedSeconds] = useState<number | null>(
    null,
  );
  const [playedBeforePause, setPlayedBeforePause] = useState<number | null>(
    null,
  );
  const [showTutorialOverlay, setShowTutorialOverlay] = useState(false);
  const [youtubeSearchResults, setYoutubeSearchResults] = useState<
    YouTubeSearchVideo[]
  >([]);
  const [youtubeSearchLoading, setYoutubeSearchLoading] = useState(false);
  const [youtubeSearchError, setYoutubeSearchError] = useState("");

  const startTimeRef = useRef<number | null>(null);
  const playbackTimeRef = useRef(0);

  const { data: playlistCategories } = useQuery(
    ["playlistCategories"],
    async () => {
      const { data } = await axios.post(`/treatment_category`);
      return data;
    },
  );

  useEffect(() => {
    if (!loading) {
      const timer = window.setTimeout(() => {
        setShowTutorialOverlay(true);
      }, 500);

      return () => window.clearTimeout(timer);
    }
  }, [loading]);

  const applyPlaylistSelection = useCallback((list: PlaylistCategory[]) => {
    if (list.length === 0) {
      setCurrentCategory(null);
      setCurrentVideo(null);
      setSelectedCategoryId(null);
      return;
    }

    const first = list[0];
    setCurrentCategory(first);
    setSelectedCategoryId(first.id);
    setItemId(first.id);
    setLevel(first.level || "");
    setIsArchived(Boolean(first.is_archived));
    setSelectedTreatmentIds(first.treatment_ids || []);
    setCurrentVideo(first.videos.length > 0 ? first.videos[0] : null);
  }, []);

  const handleData = useCallback(
    async (category?: number | string, search?: string) => {
      setLoading(true);
      try {
        const { data } = await axios.get(`/playlist/`, {
          params: { category, search },
        });
        const formattedData = normalizePlaylistResponse(data);
        setCategories(formattedData);
        applyPlaylistSelection(formattedData);
      } catch (error) {
        console.error("Error fetching playlist data:", error);
        toast.error("Failed to load playlists.");
      } finally {
        setLoading(false);
      }
    },
    [applyPlaylistSelection],
  );

  useEffect(() => {
    const fetchBootData = async () => {
      try {
        const [treatmentResponse, treatmentPlanResponse, userResponse] =
          await Promise.all([
            axios.get("/treatment_category"),
            axios.get("/treatment"),
            axios.get("/user"),
          ]);

        setTreatments(
          treatmentResponse.data.map((plan: any) => ({
            label: plan.category_name,
            value: plan.id,
          })),
        );

        setTreatmentPlans(
          treatmentPlanResponse.data.treatments.map((plan: any) => ({
            ...plan,
            label: plan.description,
            value: plan.id,
          })),
        );

        setUserId(userResponse.data?.[1]?.medicube_user_id || "");
        setPracticeId(userResponse.data?.[1]?.medicube_practice_id || "");
      } catch (error) {
        console.error("Error fetching page data:", error);
      }
    };

    handleData(undefined, "");
    fetchBootData();
  }, [handleData]);

  useEffect(() => {
    if (currentCategory?.treatment_ids) {
      setSelectedTreatmentIds(currentCategory.treatment_ids);
    }
  }, [currentCategory]);

  const fetchPlaylistsByTreatment = useCallback(
    async (treatmentId: string) => {
      setLoading(true);
      try {
        const { data } = await axios.get(`/playlist/`, {
          params: { category: treatmentId },
        });
        const formattedData = normalizePlaylistResponse(data);
        setCategories(formattedData);
        applyPlaylistSelection(formattedData);
      } catch (error) {
        console.error("Error fetching filtered playlists:", error);
        toast.error("Failed to filter playlists.");
      } finally {
        setLoading(false);
      }
    },
    [applyPlaylistSelection],
  );

  const handleSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        if (!value.trim()) {
          handleData();
          return;
        }

        setSearchLoading(true);
        try {
          const { data } = await axios.get(`/playlist/`, {
            params: { search: value.trim() },
          });
          const formattedData = normalizePlaylistResponse(data);
          setCategories(formattedData);
          applyPlaylistSelection(formattedData);
        } catch (error) {
          console.error("Error searching playlists:", error);
          toast.error("Failed to search playlists.");
        } finally {
          setSearchLoading(false);
        }
      }, 300),
    [applyPlaylistSelection, handleData],
  );

  const runYoutubeSearch = useCallback(async (value: string) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      setYoutubeSearchResults([]);
      setYoutubeSearchError("");
      return;
    }

    const shouldSearchYoutube =
      trimmedValue.length >= 2 || isYoutubeLink(trimmedValue);
    if (!shouldSearchYoutube) return;

    setYoutubeSearchLoading(true);
    setYoutubeSearchError("");

    const params = isLikelyUrl(trimmedValue)
      ? { url: trimmedValue, q: trimmedValue, search: trimmedValue }
      : { q: trimmedValue, search: trimmedValue };

    try {
      const { data } = await axios.get("/playlist/youtube/search", { params });
      setYoutubeSearchResults(
        normalizeYoutubeSearchResponse(data, trimmedValue),
      );
    } catch (firstError) {
      try {
        const { data } = await axios.get("/youtube/search", { params });
        setYoutubeSearchResults(
          normalizeYoutubeSearchResponse(data, trimmedValue),
        );
      } catch (secondError) {
        const fallbackVideo = createYoutubeFallbackFromUrl(trimmedValue);

        if (fallbackVideo) {
          setYoutubeSearchResults([fallbackVideo]);
          setYoutubeSearchError("");
        } else {
          console.error(
            "Error searching YouTube videos:",
            firstError,
            secondError,
          );
          setYoutubeSearchResults([]);
          setYoutubeSearchError("No YouTube results found.");
        }
      }
    } finally {
      setYoutubeSearchLoading(false);
    }
  }, []);

  const handleYoutubeSearch = useMemo(
    () => debounce((value: string) => runYoutubeSearch(value), 450),
    [runYoutubeSearch],
  );

  const handleSearchInputPaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    const pastedValue = event.clipboardData.getData("text").trim();
    if (!pastedValue || !isLikelyUrl(pastedValue)) return;

    window.setTimeout(() => {
      setSearchParam(pastedValue);
      handleSearch.cancel();
      handleYoutubeSearch.cancel();
      void runYoutubeSearch(pastedValue);
    }, 0);
  };

  useEffect(() => {
    handleSearch(searchParam);
    handleYoutubeSearch(searchParam);
    return () => {
      handleSearch.cancel();
      handleYoutubeSearch.cancel();
    };
  }, [searchParam, handleSearch, handleYoutubeSearch]);

  useEffect(() => {
    if (selectedTreatment) {
      fetchPlaylistsByTreatment(selectedTreatment);
    } else if (!searchParam.trim()) {
      handleData();
    }
  }, [selectedTreatment, fetchPlaylistsByTreatment]);

  const availableTreatmentFilters = useMemo(() => {
    const usedCategoryIds = new Set(
      categories
        .map((item) => item.treatment_category_id)
        .filter((id) => id !== null && id !== undefined),
    );

    return treatments.filter((treatment) =>
      usedCategoryIds.has(treatment.value),
    );
  }, [categories, treatments]);

  const filteredPlaylists = useMemo(() => {
    if (filter === "all") return categories;
    const result = categories.filter(
      (cat) => cat.type === filter || cat.level === filter,
    );
    return result.length > 0 ? result : [];
  }, [categories, filter]);

  const logUserAction = (event: string, description: string, data: any) => {
    if (!userid) return;

    axios
      .post(`/log`, {
        type: "info",
        description,
        event,
        data,
        practice_id: practiceId,
        user_id: userid,
      })
      .catch((error) => console.error("Error logging user action:", error));
  };

  const onCategorySelect = async (category: PlaylistCategory) => {
    setSelectedCategoryId(category.id);

    try {
      const response = await axios.get(`/playlist/${category.id}`);
      setLevel(response.data.level);
      setItemId(response.data.id);
      setIsArchived(Boolean(response.data.is_archived));
      setSelectedCategoryName(response.data.treatment_category_name || "");

      const freshCategory: PlaylistCategory = {
        ...category,
        level: response.data.level || category.level,
        is_archived: response.data.is_archived,
        treatment_ids:
          response.data.treatment_ids || category.treatment_ids || [],
        treatment_category_id:
          response.data.treatment_category_id || category.treatment_category_id,
        videos: (response.data.playist_items || category.videos || []).map(
          (video: any) => ({
            id: video.id,
            title: video.title || "Untitled video",
            url: video.url,
            thumbnail: video.thumbnail || "",
            description: video.description || "",
            platform: video.platform || "youtube",
            duration: video.duration || "00:00:00",
            type: video.type || "mp4",
          }),
        ),
      };

      setCurrentCategory(freshCategory);
      setSelectedTreatmentIds(freshCategory.treatment_ids || []);
      setCurrentVideo(
        freshCategory.videos.length > 0 ? freshCategory.videos[0] : null,
      );
      logUserAction("Treatment Selected", "User clicked on a treatment", {
        treatment: category.name,
      });
    } catch (error) {
      console.error("Error fetching playlist:", error);
      toast.error("Failed to load playlist.");
    }
  };

  const handleSubmitPlaylistSave = async (formValues: any) => {
    try {
      const categoryIdInt = parseInt(formValues.category_id, 10);
      const response = await axios.post("/playlist/", {
        title: formValues.playlistName,
        treatment_category_id: categoryIdInt,
        treatment_ids: selectedTreatmentIdsNew,
      });

      const playlistId = response.data;
      const getResponse = await axios.get(`/playlist/${playlistId}`);

      const newPlaylist: PlaylistCategory = {
        id: getResponse.data.id,
        name: getResponse.data.title || formValues.playlistName,
        treatment_category_id:
          getResponse.data.treatment_category_id || categoryIdInt,
        treatment_ids:
          getResponse.data.treatment_ids || selectedTreatmentIdsNew,
        videos: [],
        level: getResponse.data.level || "user",
        type: "default_user_playlists",
        is_archived: false,
      };

      setCategories((prev) => [newPlaylist, ...prev]);
      setCurrentCategory(newPlaylist);
      setSelectedCategoryId(newPlaylist.id);
      setSelectedCategoryName(getResponse.data.treatment_category_name || "");
      setSelectedTreatmentIds(newPlaylist.treatment_ids || []);
      setCurrentVideo(null);
      setItemId(newPlaylist.id);
      setOpenCreatePlaylistModal(false);
      toast.success("Playlist created successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Error saving playlist.");
      console.error("Error saving playlist:", error);
    }
  };

  const updatePlaylist = async (values: any) => {
    if (!currentCategory) return;

    try {
      await axios.put(`/playlist/${currentCategory.id}`, {
        title: values.playlistName,
        treatment_category_id: values.category_id,
        treatment_ids: selectedTreatmentIds,
        level: values.options,
      });

      const response = await axios.get(`/playlist/${currentCategory.id}`);
      const updatedCategory: PlaylistCategory = {
        ...currentCategory,
        name: response.data.title || values.playlistName,
        level: response.data.level || values.options,
        treatment_category_id:
          response.data.treatment_category_id || values.category_id,
        treatment_ids: response.data.treatment_ids || selectedTreatmentIds,
        is_archived: response.data.is_archived,
      };

      setCategories((prev) =>
        prev.map((item) =>
          item.id === updatedCategory.id ? updatedCategory : item,
        ),
      );
      setCurrentCategory(updatedCategory);
      setLevel(updatedCategory.level || "");
      setIsArchived(Boolean(updatedCategory.is_archived));
      setSelectedCategoryName(response.data.treatment_category_name || "");
      toast.success("Playlist edited successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Error updating playlist.");
      console.error("Error updating playlist:", error);
    }
  };

  const clonePlaylist = async (values: any) => {
    if (!currentCategory) return;

    if (!values.treatment_ids || values.treatment_ids.length === 0) {
      toast.error("Please select at least one treatment.");
      return;
    }

    if (!values.options?.trim()) {
      toast.error("Please select a level.");
      return;
    }

    try {
      const response = await axios.put(
        `/playlist/${currentCategory.id}/copy_global_playlist_to_private`,
        {
          title: values.playlistName,
          treatment_category_id: values.category_id,
          treatment_ids: values.treatment_ids.map((t: any) => t.value),
          level: values.options,
        },
      );

      const newPlaylist: PlaylistCategory = {
        id: response.data.id,
        name: values.playlistName,
        videos: (response.data.playist_items || []).map((video: any) => ({
          id: video.id,
          title: video.title,
          url: video.url,
          thumbnail: video.thumbnail || "",
          description: video.description || "",
          type: video.type,
          platform: video.platform,
          duration: video.duration,
        })),
        level: response.data.level,
        type:
          response.data.level === "practice"
            ? "default_practice_playlists"
            : "default_user_playlists",
        is_default: response.data.is_default,
        treatment_category_id: response.data.treatment_category_id,
        treatment_ids: response.data.treatment_ids || [],
      };

      setCategories((prev) => [newPlaylist, ...prev]);
      setCurrentCategory(newPlaylist);
      setSelectedCategoryId(newPlaylist.id);
      setItemId(newPlaylist.id);
      setLevel(newPlaylist.level || "");
      setCurrentVideo(newPlaylist.videos[0] || null);
      toast.success("Playlist created successfully");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.detail ||
          "An error occurred while cloning the playlist.",
      );
      console.error("Error cloning playlist:", error);
    }
  };

  const handleSubmitPlaylistUpdate = (values: any) => {
    if (currentCategory?.level === "global") {
      clonePlaylist(values);
      return;
    }

    updatePlaylist(values);
  };

  const handleTreatmentChange = (_event: any, newValue: TreatmentOption[]) => {
    setSelectedTreatmentIds(newValue.map((option) => Number(option.value)));
  };

  const handleTreatmentChangeNew = (
    _event: any,
    newValue: TreatmentOption[],
  ) => {
    setSelectedTreatmentIdsNew(newValue.map((option) => Number(option.value)));
  };

  const handleSubmitPlaylistItemSave = async (newVideo: PlaylistVideo) => {
    try {
      if (!currentCategory) return;

      const updatedCategory = {
        ...currentCategory,
        videos: [...(currentCategory.videos || []), newVideo],
      };

      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === updatedCategory.id ? updatedCategory : cat,
        ),
      );
      setCurrentCategory(updatedCategory);
      setCurrentVideo(newVideo);
      setOpenAddVideoModal(false);
    } catch (error) {
      toast.error("Error saving playlist item.");
      console.error("Error saving playlist item:", error);
    }
  };

  const savePlaylistItemsToServer = async (nextVideos: PlaylistVideo[]) => {
    if (!currentCategory) return false;

    const payload = nextVideos.map(({ id, thumbnail, ...video }) => ({
      title: video.title || "",
      description: video.description || "",
      url: video.url,
      thumbnail: thumbnail || "",
      platform: video.platform || "youtube",
      duration: video.duration || "00:00:00",
      type: video.type || "youtube",
    }));

    const response = await axios.put(
      `playlist/${itemId}/save_playlist_items`,
      payload,
    );
    return response.status === 200 && response.data === true;
  };

  const handleAddYoutubeResult = async (video: YouTubeSearchVideo) => {
    if (!currentCategory) {
      toast.error("Please select a playlist first.");
      return;
    }

    if (isReadOnly) {
      toast.error("This playlist cannot be edited.");
      return;
    }

    const alreadyExists = currentCategory.videos.some(
      (item) => item.url === video.url,
    );
    if (alreadyExists) {
      toast.info("This video is already in the selected playlist.");
      setCurrentVideo(video);
      return;
    }

    try {
      const nextVideos = [...(currentCategory.videos || []), video];
      const saved = await savePlaylistItemsToServer(nextVideos);

      if (!saved) {
        toast.error("Failed to add the YouTube video.");
        return;
      }

      const updatedCategory = { ...currentCategory, videos: nextVideos };
      setCategories((prev) =>
        prev.map((category) =>
          category.id === updatedCategory.id ? updatedCategory : category,
        ),
      );
      setCurrentCategory(updatedCategory);
      setCurrentVideo(video);
      toast.success("YouTube video added to playlist.");
    } catch (error) {
      console.error("Error adding YouTube video:", error);
      toast.error("An error occurred while adding the YouTube video.");
    }
  };

  const handleDeletePlaylist = (playlistId: number | string) => {
    axios
      .delete(`/playlist/${playlistId}`)
      .then(() => {
        const nextCategories = categories.filter(
          (category) => category.id !== playlistId,
        );
        setCategories(nextCategories);
        applyPlaylistSelection(nextCategories);
        toast.success("Playlist deleted successfully");
      })
      .catch((error) => {
        toast.error("Error deleting playlist.");
        console.error("Error deleting playlist:", error);
      });
  };

  const handleDelete = async (videoToDelete: PlaylistVideo) => {
    if (!currentCategory) return;

    try {
      const updatedVideos = currentCategory.videos.filter(
        (video) => video !== videoToDelete,
      );
      const payload = updatedVideos.map(({ id, thumbnail, ...video }) => ({
        title: video.title || "",
        description: video.description || "",
        url: video.url,
        thumbnail: thumbnail || "",
        platform: video.platform || "s3",
        duration: video.duration || "00:00:00",
        type: video.type || "mp4",
      }));

      const response = await axios.put(
        `playlist/${itemId}/save_playlist_items`,
        payload,
      );

      if (response.status === 200 && response.data === true) {
        const updatedCategory = { ...currentCategory, videos: updatedVideos };
        setCategories((prev) =>
          prev.map((category) =>
            category.id === updatedCategory.id ? updatedCategory : category,
          ),
        );
        setCurrentCategory(updatedCategory);
        setCurrentVideo(updatedVideos[0] || null);
        toast.success("Playlist item deleted successfully");
      } else {
        toast.error("Failed to delete playlist item.");
      }
    } catch (error) {
      console.error("Error deleting playlist item:", error);
      toast.error("An error occurred while deleting the playlist item.");
    }
  };

  const resetTimeTracking = () => {
    startTimeRef.current = Date.now();
    playbackTimeRef.current = 0;
  };

  const logCurrentVideoTime = () => {
    if (!startTimeRef.current || !currentVideo) return;

    const endTime = Date.now();
    const duration = (endTime - startTimeRef.current) / 1000;
    const actualPlaybackTime = playbackTimeRef.current;

    logUserAction("Video Watched", "User stopped watching a video", {
      video: currentVideo.title,
      duration,
      actualPlaybackTime,
    });
  };

  const onVideoSelect = (video: PlaylistVideo) => {
    logCurrentVideoTime();
    setCurrentVideo(video);
    resetTimeTracking();
    logUserAction("Video Selected", "User clicked on a Video", {
      video: video.title,
    });
  };

  const handleVideoProgress = (state: any) => {
    setLastPlayedSeconds(state.playedSeconds);
    playbackTimeRef.current = state.playedSeconds;
  };

  const handleVideoPause = () => {
    setPlayedBeforePause(lastPlayedSeconds);
    logCurrentVideoTime();
    resetTimeTracking();
  };

  const handleVideoResume = () => {
    const newPlayedSeconds = lastPlayedSeconds || 0;
    const calculatedSeekTime = newPlayedSeconds - (playedBeforePause || 0);
    void calculatedSeekTime;
  };

  const extractTikTokId = (url?: string | null) => {
    if (!url) return null;
    const match = url.match(/video\/(\d+)/);
    return match ? match[1] : null;
  };

  const displayLevel = currentCategory?.level || level;
  const isReadOnly = displayLevel === "global" || isArchived;

  if (loading) {
    return (
      <div className={styles.videoLoadingContainer}>
        <div className={styles.videoLoadingCard}>
          <Lottie
            animationData={videoLoadingAnimation}
            style={{ width: 240, height: 240 }}
          />
          <img src="/logo.png" alt="Loading" className={styles.loadingLogo} />
          <p className={styles.loadingText}>Loading your video library...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />

      <main className={styles.galleryPage}>
        <div className={styles.galleryShell}>
          <div className={styles.topBar}>
            <h1 className={styles.pageTitle}>Video Gallery</h1>
            <Button
              variant="contained"
              startIcon={<Add />}
              className={styles.primaryBtn}
              onClick={() => setOpenCreatePlaylistModal(true)}
              data-tour="new-playlist-button"
            >
              New Playlist
            </Button>
          </div>

          <div className={styles.galleryGrid}>
            <aside className={styles.sidebar}>
              <div className={styles.searchBox} data-tour="playlist-search">
                <Search className={styles.searchIcon} fontSize="small" />
                <input
                  value={searchParam}
                  onChange={(event) => setSearchParam(event.target.value)}
                  onPaste={handleSearchInputPaste}
                  placeholder="Search playlists or YouTube..."
                />
                {searchLoading && <span className={styles.searchDot} />}
              </div>

              <Select
                value={selectedTreatment}
                onChange={(event) =>
                  setSelectedTreatment(event.target.value as string)
                }
                displayEmpty
                fullWidth
                size="small"
                className={styles.filterSelect}
                data-tour="treatment-filter"
              >
                <MenuItem value="">All</MenuItem>
                {availableTreatmentFilters.map((treatment) => (
                  <MenuItem
                    key={treatment.value}
                    value={String(treatment.value)}
                  >
                    {treatment.label}
                  </MenuItem>
                ))}
              </Select>

              <div className={styles.hiddenFilterRow}>
                <button
                  type="button"
                  className={
                    filter === "all"
                      ? styles.activeFilterChip
                      : styles.filterChip
                  }
                  onClick={() => setFilter("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className={
                    filter === "practice"
                      ? styles.activeFilterChip
                      : styles.filterChip
                  }
                  onClick={() => setFilter("practice")}
                >
                  Practice
                </button>
                <button
                  type="button"
                  className={
                    filter === "user"
                      ? styles.activeFilterChip
                      : styles.filterChip
                  }
                  onClick={() => setFilter("user")}
                >
                  User
                </button>
                <button
                  type="button"
                  className={
                    filter === "global"
                      ? styles.activeFilterChip
                      : styles.filterChip
                  }
                  onClick={() => setFilter("global")}
                >
                  Global
                </button>
              </div>

              <div className={styles.playlistList} data-tour="playlist-list">
                {filteredPlaylists.length === 0 ? (
                  <div className={styles.emptyListState}>
                    No playlists found.
                  </div>
                ) : (
                  filteredPlaylists.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      className={`${styles.playlistItem} ${
                        selectedCategoryId === category.id
                          ? styles.activePlaylistItem
                          : ""
                      }`}
                      onClick={() => onCategorySelect(category)}
                    >
                      <span className={styles.playlistItemTitle}>
                        {category.name}
                      </span>
                      <span className={styles.playlistItemMeta}>
                        <span
                          className={`${styles.levelBadge} ${getLevelClassName(category.level)}`}
                        >
                          {getLevelLabel(category.level)}
                        </span>
                        <span>{category.videos?.length || 0} videos</span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </aside>

            <section className={styles.contentArea}>
              <div className={styles.playerFrame} data-tour="video-player">
                {currentVideo?.platform === "tiktok" ? (
                  <iframe
                    id="tiktok-player"
                    src={`https://www.tiktok.com/player/v1/${extractTikTokId(currentVideo.url)}?music_info=1&description=1`}
                    width="100%"
                    height="462"
                    allow="autoplay; fullscreen"
                    title={currentVideo.title}
                    className={styles.tiktokFrame}
                  />
                ) : currentVideo ? (
                  <ReactPlayerMain
                    url={currentVideo.url}
                    controls
                    width="100%"
                    height="462px"
                    onStart={resetTimeTracking}
                    onProgress={handleVideoProgress}
                    onPause={handleVideoPause}
                    onEnded={logCurrentVideoTime}
                    onPlay={handleVideoResume}
                    className={styles.reactPlayer}
                  />
                ) : (
                  <div className={styles.noVideoState}>
                    <SmartDisplay className={styles.noVideoIcon} />
                    <p>No videos in this playlist yet.</p>
                    {currentCategory && !isReadOnly && (
                      <Button
                        variant="contained"
                        className={styles.primaryBtn}
                        onClick={() => setOpenAddVideoModal(true)}
                        data-tour="add-video-button"
                      >
                        Add first video
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {searchParam.trim() && (
                <div
                  className={styles.youtubeSearchCard}
                  data-tour="youtube-search-results"
                >
                  <div className={styles.cardHeaderRow}>
                    <div className={styles.cardTitleGroup}>
                      <YouTube
                        className={styles.youtubeIcon}
                        fontSize="small"
                      />
                      <h2>YouTube Search Results</h2>
                      <Chip
                        size="small"
                        label={
                          youtubeSearchLoading
                            ? "Searching..."
                            : `${youtubeSearchResults.length} results`
                        }
                        className={styles.countChip}
                      />
                    </div>
                  </div>

                  {youtubeSearchError ? (
                    <div className={styles.youtubeSearchState}>
                      {youtubeSearchError}
                    </div>
                  ) : youtubeSearchLoading &&
                    youtubeSearchResults.length === 0 ? (
                    <div className={styles.youtubeSearchState}>
                      Searching YouTube...
                    </div>
                  ) : youtubeSearchResults.length === 0 ? (
                    <div className={styles.youtubeSearchState}>
                      Search YouTube by title or paste a YouTube link.
                    </div>
                  ) : (
                    <div className={styles.youtubeResultGrid}>
                      {youtubeSearchResults.map((video, index) => {
                        const isAdded = Boolean(
                          currentCategory?.videos?.some(
                            (item) => item.url === video.url,
                          ),
                        );

                        return (
                          <div
                            key={`${video.url}-${index}`}
                            className={styles.youtubeResultCard}
                            role="button"
                            tabIndex={0}
                            onClick={() => setCurrentVideo(video)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") setCurrentVideo(video);
                            }}
                          >
                            <div className={styles.youtubeResultThumb}>
                              {video.thumbnail ? (
                                <img src={video.thumbnail} alt={video.title} />
                              ) : (
                                <VideocamOutlined fontSize="large" />
                              )}
                              <span className={styles.videoCardPlayOverlay}>
                                <PlayArrow fontSize="small" />
                              </span>
                            </div>

                            <div className={styles.youtubeResultBody}>
                              <h3>{video.title}</h3>
                              <p>{video.channelTitle || "YouTube"}</p>
                              <Button
                                variant={isAdded ? "outlined" : "contained"}
                                size="small"
                                className={
                                  isAdded
                                    ? styles.addedYoutubeBtn
                                    : styles.addYoutubeBtn
                                }
                                disabled={
                                  isAdded || isReadOnly || !currentCategory
                                }
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void handleAddYoutubeResult(video);
                                }}
                              >
                                {isAdded ? "Added" : "Add to Playlist"}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className={styles.videoListCard} data-tour="video-list-card">
                <div className={styles.cardHeaderRow}>
                  <div className={styles.cardTitleGroup}>
                    <h2>{currentCategory?.name || "No playlist selected"}</h2>
                    <Chip
                      size="small"
                      label={`${currentCategory?.videos?.length || 0} videos`}
                      className={styles.countChip}
                    />
                  </div>

                  {currentCategory && !isReadOnly && (
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      className={styles.primaryBtn}
                      onClick={() => setOpenAddVideoModal(true)}
                      data-tour="add-video-button"
                    >
                      Add Video
                    </Button>
                  )}
                </div>

                <div className={styles.videoGrid}>
                  {(currentCategory?.videos || []).length === 0 ? (
                    <div className={styles.emptyVideos}>
                      No videos added yet.
                    </div>
                  ) : (
                    currentCategory?.videos.map((video, index) => (
                      <div
                        key={`${video.url}-${index}`}
                        className={`${styles.videoCard} ${
                          currentVideo?.url === video.url
                            ? styles.activeVideoCard
                            : ""
                        }`}
                        onClick={() => onVideoSelect(video)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") onVideoSelect(video);
                        }}
                      >
                        <div className={styles.videoCardThumb}>
                          {video.thumbnail ? (
                            <img src={video.thumbnail} alt={video.title} />
                          ) : (
                            <VideocamOutlined fontSize="large" />
                          )}
                          <span className={styles.videoCardPlayOverlay}>
                            <PlayArrow fontSize="small" />
                          </span>
                          {currentVideo?.url === video.url && (
                            <span className={styles.nowPlayingBadge}>
                              Playing
                            </span>
                          )}
                        </div>

                        <div className={styles.videoCardBody}>
                          <h3>{video.title}</h3>
                          <p>
                            <span>{video.platform || "video"}</span>
                            <span>{video.duration || "0:00"}</span>
                          </p>
                        </div>

                        {!isReadOnly && (
                          <IconButton
                            size="small"
                            className={styles.cardDeleteBtn}
                            onClick={(event) => {
                              event.stopPropagation();
                              confirmAlert({
                                title: "Delete video?",
                                message:
                                  "This will remove this item from the playlist.",
                                buttons: [
                                  {
                                    label: "Delete",
                                    onClick: () => handleDelete(video),
                                  },
                                  { label: "Cancel", onClick: () => undefined },
                                ],
                              });
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {currentCategory && (
                <div
                  className={styles.settingsCard}
                  data-tour="playlist-settings-card"
                >
                  <div className={styles.settingsHeader}>
                    <Tune fontSize="small" />
                    <h2>Playlist Settings</h2>
                    {isArchived && (
                      <span className={styles.archivedPill}>
                        <Archive fontSize="small" /> Archived
                      </span>
                    )}
                  </div>

                  <Form
                    key={currentCategory.id}
                    onSubmit={handleSubmitPlaylistUpdate}
                    render={({ handleSubmit }) => (
                      <form
                        onSubmit={handleSubmit}
                        className={styles.settingsForm}
                      >
                        <div className={styles.formGridTwo}>
                          <Field
                            name="playlistName"
                            initialValue={currentCategory.name || ""}
                          >
                            {({ input }) => (
                              <TextField
                                label="Name"
                                variant="outlined"
                                fullWidth
                                size="small"
                                {...input}
                                disabled={isArchived}
                              />
                            )}
                          </Field>

                          <Field
                            name="category_id"
                            initialValue={
                              currentCategory.treatment_category_id || ""
                            }
                          >
                            {({ input }) => (
                              <TextField
                                select
                                label="Category"
                                variant="outlined"
                                fullWidth
                                size="small"
                                {...input}
                                disabled={isArchived}
                              >
                                {playlistCategories?.map((category: any) => (
                                  <MenuItem
                                    value={category.id}
                                    key={category.id}
                                  >
                                    {category.category_name}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                          </Field>
                        </div>

                        <Autocomplete
                          name="treatment_ids"
                          label="Linked Treatments"
                          multiple
                          required
                          options={treatmentPlans}
                          getOptionLabel={(option: any) => option.label || ""}
                          isOptionEqualToValue={(option: any, value: any) =>
                            option.value === value.value ||
                            option.value === value
                          }
                          value={treatmentPlans.filter((option) =>
                            selectedTreatmentIds.includes(Number(option.value)),
                          )}
                          onChange={handleTreatmentChange}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Linked Treatments"
                              size="small"
                              disabled={isArchived}
                              error={selectedTreatmentIds.length === 0}
                              helperText={
                                selectedTreatmentIds.length === 0
                                  ? "Select at least one treatment"
                                  : ""
                              }
                            />
                          )}
                        />

                        <div className={styles.settingsFooterRow}>
                          <FormControl
                            component="fieldset"
                            className={styles.visibilityControl}
                            data-tour="visibility-section"
                          >
                            <FormLabel component="legend">Visibility</FormLabel>
                            <Field
                              name="options"
                              initialValue={currentCategory.level || "user"}
                            >
                              {({ input }) => (
                                <RadioGroup
                                  row
                                  {...input}
                                  value={input.value}
                                  onChange={input.onChange}
                                >
                                  <FormControlLabel
                                    value="user"
                                    control={<Radio size="small" />}
                                    label="User only"
                                    disabled={isArchived}
                                  />
                                  <FormControlLabel
                                    value="practice"
                                    control={<Radio size="small" />}
                                    label="Practice"
                                    disabled={isArchived}
                                  />
                                  {currentCategory.level === "global" && (
                                    <FormControlLabel
                                      value="global"
                                      control={<Radio size="small" />}
                                      label="Global"
                                      disabled
                                    />
                                  )}
                                </RadioGroup>
                              )}
                            </Field>
                          </FormControl>

                          <div
                            className={styles.settingsActions}
                            data-tour="playlist-actions"
                          >
                            {currentCategory.level !== "global" &&
                              !isArchived && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  startIcon={<Delete />}
                                  className={styles.deletePlaylistBtn}
                                  onClick={() =>
                                    confirmAlert({
                                      title: "Delete playlist?",
                                      message:
                                        "This will remove the playlist and its configuration for this account.",
                                      buttons: [
                                        {
                                          label: "Delete",
                                          onClick: () =>
                                            handleDeletePlaylist(
                                              currentCategory.id,
                                            ),
                                        },
                                        {
                                          label: "Cancel",
                                          onClick: () => undefined,
                                        },
                                      ],
                                    })
                                  }
                                >
                                  Delete
                                </Button>
                              )}

                            <Button
                              type="submit"
                              variant="contained"
                              className={styles.primaryBtn}
                              disabled={isArchived}
                            >
                              Save Changes
                            </Button>
                          </div>
                        </div>
                      </form>
                    )}
                  />
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      <CreatePlaylistModal
        open={openCreatePlaylistModal}
        onClose={() => setOpenCreatePlaylistModal(false)}
        onSubmit={handleSubmitPlaylistSave}
        categories={playlistCategories || []}
        treatmentPlans={treatmentPlans}
        onTreatmentChange={handleTreatmentChangeNew}
      />

      {currentCategory && selectedCategoryId && (
        <PlaylistModal
          open={openAddVideoModal}
          onClose={() => setOpenAddVideoModal(false)}
          onSubmit={handleSubmitPlaylistItemSave}
          playlistId={selectedCategoryId}
          currentCategory={currentCategory}
        />
      )}

      <VideoTutorialOverlay
        open={showTutorialOverlay}
        onClose={() => setShowTutorialOverlay(false)}
      />
    </>
  );
};

export default VideoManager;
